import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Tooltip,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ReceiptLong as InvoiceIcon,
  ShoppingCart as PurchaseIcon,
  Payments as ExpenseIcon,
  Dashboard as DashboardIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { isAdminEmail } from '../config/admin';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate, Navigate } from 'react-router-dom';
// Firebase imports removed
// import { db } from '../firebase/config';
// import { collection, query, where, getDocs, Timestamp, doc, getDoc, orderBy, limit } from 'firebase-shim';
import indexedDBService from '../utils/indexedDBService';
import { getMonthlyTotal } from '../utils/monthlyTotals';
import CurrentMonthInsightsWidget from '../components/dashboard/CurrentMonthInsightsWidget';
import SalesExpensesWidget from '../components/dashboard/SalesExpensesWidget';
import { useWidgetSettings, WidgetName } from '../context/WidgetSettingsContext';
import OutstandingInvoicesWidget from '../components/dashboard/OutstandingInvoicesWidget';
import ClientReceivablesWidget from '../components/dashboard/ClientReceivablesWidget';
import TopSellingItemsWidget from '../components/dashboard/TopSellingItemsWidget';
import LowStockAlertsWidget from '../components/dashboard/LowStockAlertsWidget';
import { useSetupGuide } from '../context/SetupGuideContext';
import SetupGuide from '../components/SetupGuide';
import { useZeroClickAutomation } from '../hooks/useZeroClickAutomation';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

interface MonthlyData {
  currentMonth: number;
  previousMonth: number;
  percentageChange: number;
  chartData: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      borderColor: string;
      backgroundColor: string;
    }[];
  };
}

const PurchaseInsights: React.FC<{ userId: string }> = ({ userId }) => {
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [pendingAmount, setPendingAmount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchPurchaseData = async () => {
      try {
        // Get all purchases from IndexedDB
        const purchases = await indexedDBService.getAllItems<any>('purchases');
        const userPurchases = purchases.filter(p => p.userId === userId);

        if (!isMounted) return;

        let total = 0;
        let pending = 0;

        userPurchases.forEach(purchase => {
          total += purchase.totalAmount || 0;

          if ((purchase.status || '').toLowerCase() === 'pending') {
            pending += purchase.totalAmount || 0;
          }
        });

        setTotalPurchases(total);
        setPendingAmount(pending);
      } catch (error) {
        console.error('Error fetching purchase data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (userId) {
      fetchPurchaseData();
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [userId]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 4,
        height: '100%',
        background: 'linear-gradient(to right, #ff9966, #ff5e62)',
        color: 'white',
      }}
    >
      <Box display="flex" flexDirection="column" height="100%">
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Purchase Insights
        </Typography>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="80%">
            <CircularProgress color="inherit" size={30} />
          </Box>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2">Total Purchases</Typography>
              <Typography variant="h6" fontWeight="bold">₹{totalPurchases.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">Pending to Vendors</Typography>
              <Typography variant="h6" fontWeight="bold">₹{pendingAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</Typography>
            </Box>
            <Box mt="auto" pt={2}>
              <Button
                variant="contained"
                fullWidth
                component={Link}
                to="/purchases"
                sx={{
                  mt: 2,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
              >
                View Purchases
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

const Dashboard: React.FC = () => {
  const { userProfile, currentUser } = useAuth();
  
  // Run zero-click automation
  useZeroClickAutomation();

  // Redirect admin to admin dashboard
  const { isWidgetVisible } = useWidgetSettings();
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const navigate = useNavigate();
  const [recentTransactions, setRecentTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [salesData, setSalesData] = useState<MonthlyData>({
    currentMonth: 0,
    previousMonth: 0,
    percentageChange: 0,
    chartData: {
      labels: [],
      datasets: []
    }
  });
  const [purchaseData, setPurchaseData] = useState<MonthlyData>({
    currentMonth: 0,
    previousMonth: 0,
    percentageChange: 0,
    chartData: {
      labels: [],
      datasets: []
    }
  });

  // Add setup guide state
  // Add setup guide state
  const { } = useSetupGuide();
  const [showSetupGuide, setShowSetupGuide] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleCreateInvoice = () => {
    navigate('/invoices/new');
    setFabOpen(false);
  };

  const handleCreatePurchase = () => {
    navigate('/purchases', { state: { openNewPurchase: true } });
    setFabOpen(false);
  };

  const handleCreateExpense = () => {
    navigate('/expenses', { state: { openNewExpense: true } });
    setFabOpen(false);
  };

  const fabActions = [
    { icon: <InvoiceIcon />, name: 'Create Invoice', action: handleCreateInvoice },
    { icon: <PurchaseIcon />, name: 'Create Purchase', action: handleCreatePurchase },
    { icon: <ExpenseIcon />, name: 'Create Expense', action: handleCreateExpense },
  ];

  // Add a retry button to the error message
  const handleRetryLoad = () => {
    setRetryCount(prev => prev + 1);
    setError(null);
  };

  // Main dashboard data loading
  useEffect(() => {
    let isMounted = true;
    const fetchDashboardData = async () => {
      setLoading(true);

      try {


        // Load recent transactions
        try {
          // Fetch from IndexedDB
          const [invoices, purchases, expenses] = await Promise.all([
            indexedDBService.getAllItems<any>('invoices'),
            indexedDBService.getAllItems<any>('purchases'),
            indexedDBService.getAllItems<any>('expenses')
          ]);

          // Combine and filter by user
          const allDocs = [
            ...invoices.filter(i => i.userId === currentUser?.uid).map(i => ({ ...i, type: 'invoice' })),
            ...purchases.filter(p => p.userId === currentUser?.uid).map(p => ({ ...p, type: 'purchase' })),
            ...expenses.filter(e => e.userId === currentUser?.uid).map(e => ({ ...e, type: 'expense' }))
          ];

          // Sort by date desc
          allDocs.sort((a, b) => {
            const dateA = new Date(a.date || a.createdAt).getTime();
            const dateB = new Date(b.date || b.createdAt).getTime();
            return dateB - dateA;
          });

          // Take top 5
          const recent = allDocs.slice(0, 5);

          if (!isMounted) return;
          setRecentTransactions(recent);

        } catch (transactionError) {
          console.error('Error fetching transactions:', transactionError);
          // Continue execution even if transactions fail
        }

        // Fetch monthly summary documents
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1; // 1-indexed for monthlyTotals utils
        const currentYear = currentDate.getFullYear();

        // Previous month
        const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear;

        try {
          if (!currentUser) return;

          // Fetch current month summary from local storage (or calculate it?)
          // getMonthlyTotal returns Promise<MonthlyTotal | null>
          const currentSummary = await getMonthlyTotal(currentUser.uid, currentYear, currentMonth);
          const prevSummary = await getMonthlyTotal(currentUser.uid, prevYear, prevMonth);

          if (!isMounted) return;

          // Process sales data
          const currentMonthSales = currentSummary?.totalSales || 0;
          const prevMonthSales = prevSummary?.totalSales || 0;

          const salesPercentChange = prevMonthSales !== 0
            ? ((currentMonthSales - prevMonthSales) / prevMonthSales) * 100
            : currentMonthSales > 0 ? 100 : 0;

          // Process purchase data
          const currentMonthPurchases = currentSummary?.totalPurchases || 0;
          const prevMonthPurchases = prevSummary?.totalPurchases || 0;

          const purchasesPercentChange = prevMonthPurchases !== 0
            ? ((currentMonthPurchases - prevMonthPurchases) / prevMonthPurchases) * 100
            : currentMonthPurchases > 0 ? 100 : 0;

          // Set data for widgets
          setSalesData({
            currentMonth: currentMonthSales,
            previousMonth: prevMonthSales,
            percentageChange: salesPercentChange,
            chartData: {
              labels: ['Previous Month', 'Current Month'],
              datasets: [{
                label: 'Sales',
                data: [prevMonthSales, currentMonthSales],
                borderColor: 'rgba(75, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
              }]
            }
          });

          setPurchaseData({
            currentMonth: currentMonthPurchases,
            previousMonth: prevMonthPurchases,
            percentageChange: purchasesPercentChange,
            chartData: {
              labels: ['Previous Month', 'Current Month'],
              datasets: [{
                label: 'Purchases',
                data: [prevMonthPurchases, currentMonthPurchases],
                borderColor: 'rgba(255, 99, 132, 1)',
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
              }]
            }
          });
        } catch (summaryError) {
          console.error('Error fetching monthly summaries:', summaryError);
          if (isMounted) {
            setError('Failed to load dashboard data. Please try again.');
          }
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (isMounted) {
          setError('Failed to load dashboard data. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (currentUser) {
      fetchDashboardData();
    }

    return () => {
      isMounted = false;
    };
  }, [currentUser, userProfile, retryCount]);

  // Handle profile check completion behavior (if needed in future)
  /* 
  const handleProfileCheckComplete = async () => {
    // Check if setup guide should be shown
    const shouldShowSetupGuide = await checkSetupStatus();
    if (shouldShowSetupGuide) {
      setShowSetupGuide(true);
    }
  };
  */

  // Handle refresh button click
  const handleRefresh = () => {
    setRetryCount(prev => prev + 1);
    setError(null);
  };

  // Show loading state while waiting for user data
  if (!currentUser) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Redirect admin to admin dashboard
  if (isAdminEmail(currentUser.email)) {
    return <Navigate to="/admin" replace />;
  }

  // RBAC layer enforcement
  const hasFinanceAccess = ['admin', 'accountant', 'super_admin'].includes(userProfile?.role || '') || isAdminEmail(currentUser?.email || '');

  return (
    <Box sx={{ py: 0, px: { xs: 2, md: 4 } }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
          <CircularProgress />
        </Box>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={handleRetryLoad}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          <Box mb={1} mt={2}>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
              Welcome back, <span style={{ color: '#42a5f5' }}>
                {userProfile?.authorizedRepresentative?.name?.split(' ')[0] ||
                  userProfile?.firstName ||
                  userProfile?.businessName ||
                  userProfile?.displayName?.split(' ')[0] ||
                  currentUser?.displayName?.split('@')[0] ||
                  'User'}
              </span>
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Here's what's happening with your business today.
              </Typography>


            </Box>
          </Box>

          <Grid container spacing={2.5}>
            {/* ── RESTRICTED VIEW: CASHIER/STAFF SEE ONLY INVENTORY ── */}
            {!hasFinanceAccess && (
              <Grid item xs={12}>
                <motion.div variants={itemVariants}>
                  <LowStockAlertsWidget />
                </motion.div>
              </Grid>
            )}

            {/* ── FULL ACCESS VIEW ── */}
            {hasFinanceAccess && (
              <>
                {/* Current Month Insights Widget */}
                <Grid item xs={12}>
                  <motion.div variants={itemVariants}>
                    <CurrentMonthInsightsWidget />
                  </motion.div>
                </Grid>

                {/* Cash Flow Chart (Large visual anchor) */}
                {isWidgetVisible('salesExpenses') && (
                  <Grid item xs={12} md={8}>
                    <motion.div variants={itemVariants}>
                      <SalesExpensesWidget />
                    </motion.div>
                  </Grid>
                )}

                {/* Proactive Supply Chain Protection (Low Stock) */}
                <Grid item xs={12} md={4}>
                  <motion.div variants={itemVariants}>
                    <LowStockAlertsWidget />
                  </motion.div>
                </Grid>

                {/* Advanced Analytics Row */}
                <Grid item xs={12} md={6}>
                  <motion.div variants={itemVariants}>
                    <TopSellingItemsWidget />
                  </motion.div>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Grid container spacing={2.5}>
                    {isWidgetVisible('outstandingInvoices') && (
                      <Grid item xs={12}>
                        <motion.div variants={itemVariants}>
                          <OutstandingInvoicesWidget />
                        </motion.div>
                      </Grid>
                    )}
                    {isWidgetVisible('clientReceivables') && (
                      <Grid item xs={12}>
                        <motion.div variants={itemVariants}>
                          <ClientReceivablesWidget />
                        </motion.div>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>


        </motion.div>
      )}
    </Box>
  );
};

export default Dashboard; 
